import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  RdsAlertComponent,
  RdsButtonComponent,
  RdsIconComponent,
  RdsLinkComponent,
  RdsProgressSpinnerComponent,
  RdsTabComponent,
  RdsTabsComponent,
} from '@rds/angular-components';
import { formatDate } from 'date-fns';
import { EMPTY, Observable, catchError, switchMap, tap } from 'rxjs';

import {
  GoogleAnalyticsEventCategory,
  GoogleAnalyticsEventName,
  GoogleAnalyticsService,
  GoogleCalendarParams,
  NavigateBackDirective,
  RoomBookingsCalendarComponent,
  buildParams,
} from '@bookly/shared';

import { RoomDetailsCardComponent } from '../../components/room-details-card/room-details-card.component';
import { FiltersFormService } from '../../components/room-filters-form/filters-form.service';
import { FavoriteService } from '../../services/favorite.service';
import { RoomsService } from '../../services/rooms.service';

@Component({
  selector: 'bus-room-details',
  imports: [
    RouterLink,
    RoomBookingsCalendarComponent,
    RdsLinkComponent,
    RdsIconComponent,
    RoomDetailsCardComponent,
    RdsButtonComponent,
    RdsTabsComponent,
    RdsTabComponent,
    RdsProgressSpinnerComponent,
    NavigateBackDirective,
    RdsAlertComponent,
  ],
  templateUrl: './room-details.component.html',
  styleUrl: './room-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomDetailsComponent implements OnInit {
  readonly #roomsService = inject(RoomsService);
  readonly #analyticsService = inject(GoogleAnalyticsService);
  readonly #filtersService = inject(FiltersFormService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #favoriteService = inject(FavoriteService);

  protected readonly roomDetails = this.#roomsService.roomDetails;
  protected readonly roomLayouts = this.#roomsService.roomLayouts;
  protected readonly isLoading = this.#roomsService.isRoomDetailsLoading;
  protected readonly filters = this.#filtersService.getSelectedFormData(false);
  protected readonly isFavorite = computed(
    () => this.roomDetails()?.isFavorite ?? false
  );
  protected readonly isFavoriteLoading = this.#favoriteService.isLoading;

  public ngOnInit() {
    this.#activatedRoute.params
      .pipe(
        switchMap(({ roomId }) => this.#roomsService.getRoomData(roomId)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  protected toggleFavorite(): void {
    const details = this.roomDetails();
    if (!details) return;

    const currentIsFavorite = this.isFavorite();
    const dexId = details.roomDexId;

    const request$ = (
      currentIsFavorite
        ? this.#favoriteService.removeFavorite(dexId)
        : this.#favoriteService.addFavorite(dexId)
    ) as Observable<unknown>;

    request$
      .pipe(
        tap(() => {
          this.#roomsService.updateRoomDetailsFavorite(!currentIsFavorite);
          this.#roomsService.updateRoomFavorite(dexId, !currentIsFavorite);
        }),
        catchError(() => EMPTY)
      )
      .subscribe();
  }

  protected navigateToCalendar(eventDate?: Date) {
    const details = this.roomDetails();
    if (details != null) {
      const dates = this.parseDates(eventDate);
      this.#analyticsService.trackEvent(
        GoogleAnalyticsEventName.GoogleCalendarBooking,
        GoogleAnalyticsEventCategory.GoogleCalendarBooking,
        `Room ID: ${details.roomDexId}, Room Name: ${details.roomName}, Location: ${details.siteName} ${details.buildingName} ${details.floorCode}`
      );
      window.open(
        this.buildCalendarUrl({ add: details.calendarId, dates: dates }),
        '_blank'
      );
    }
  }

  protected redirectToExternal() {
    const details = this.roomDetails();
    if (
      details != null &&
      details.externalServiceUrl &&
      details.externalServiceName
    ) {
      this.#analyticsService.trackEvent(
        GoogleAnalyticsEventName.ExternalBooking,
        GoogleAnalyticsEventCategory.ExternalServiceBooking,
        `Room ID: ${details.roomDexId}, Room Name: ${details.roomName}, Location: ${details.siteName} ${details.buildingName} ${details.floorCode}`
      );
      window.open(details.externalServiceUrl, '_blank');
    }
  }

  protected redirectToDate(date: Date) {
    if (this.roomDetails()?.manageSpace) {
      void this.#router.navigate([
        'dashboard',
        'rooms',
        this.roomDetails()?.roomDexId,
        'create',
        { eventDate: date },
      ]);
    } else {
      this.navigateToCalendar(date);
    }
  }

  protected buildCalendarUrl(calendarParams: GoogleCalendarParams): string {
    const params = buildParams(calendarParams);
    return `https://calendar.google.com/calendar/u/0/r/eventedit?${params.toString()}`;
  }

  private parseDates(eventDate?: Date): string | undefined {
    const format = "yyyyMMdd'T'HHmmss";
    if (eventDate) {
      const endDate = new Date(eventDate);
      endDate.setMinutes(eventDate.getMinutes() + 30);
      return `${formatDate(eventDate, format)}/${formatDate(endDate, format)}`;
    } else if (this.filters.fromDate && this.filters.toDate) {
      return `${formatDate(this.filters.fromDate.replace('Z', ''), format)}/${formatDate(this.filters.toDate.replace('Z', ''), format)}`;
    }
    return undefined;
  }
}
